import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import { BuildingThreeIcon, ChevronLeftIcon, ProviderIcon } from '@repo/ui/icons'
import TextLink from '@repo/ui/text-link'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { Navigate, Route, Routes, useParams } from 'react-router-dom'

import { getCenterCurrency } from '../../../api/centers/get-center-currency'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import UserNavBar from '../../../components/elements/nav-bar'
import withCenter, { WithCenterProps } from '../../../components/hoc/with-center'
import { CenterEventTable } from '../../../components/pages/center-events/center-event-table'
import OlCenterCapsuleTab from '../../../components/pages/ol-common/ol-center-capsule-tab'
import CenterEndOfStayAppreciations from '../../../components/pages/ols/centers/center-tab-views/center-end-of-stay-appreciations/center-end-of-stay-appreciations'
import CenterPlacementAndDiffusionForParents from '../../../components/pages/ols/centers/center-tab-views/center-placement/center-placement-and-diffusion-for-parents'
import CenterProgramAndActivities from '../../../components/pages/ols/centers/center-tab-views/center-program/center-program-and-activities'
import OlCenterTripsInfo from '../../../components/pages/ols/centers/center-tab-views/center-trips/ol-center-trips-info'
import OlCenterJourneyBadgeDetails from '../../../components/pages/ols/centers/ol-center-journey-badge-details'
import { getOlPermissions } from '../../../lib/constants/ol-permissions'
import { Paths } from '../../../lib/constants/path'
import CurrencyProvider from '../../../lib/contexts/currency'
import { TimezoneProvider } from '../../../lib/contexts/timezone'
import useAuth from '../../../lib/hooks/use-auth'
import useTranslate from '../../../lib/hooks/use-translate'
import { RoleEnum } from '../../../lib/types/enums/users/role.enum'
import { formatPhoneNumber } from '../../../utils/functions/format-phone-number'
import CenterGroups from '../../centers/center-details/center-groups'
import { constructCenterPath } from '../../centers/utils'
import NotFoundPage from '../../common/not-found'
import { CostOfCenterPage } from '../../ol-personal-space/centers/cost-of-center'
import { FinalCostOfCenterPage } from '../../ol-personal-space/centers/final-cost-of-center'
import { getOlName } from './utils/functions'

const OlCenterDetails = ({ center, refetch }: WithCenterProps) => {
  const { back } = useNavigator()
  const { t } = useTranslate()
  const { user } = useAuth()
  const { id } = useParams<{ id: string }>()

  const { data: centerCurrency } = useQuery({
    queryKey: ['center-currency', center?.id],
    queryFn: () => {
      if (center) return getCenterCurrency(center?.id)
    },
    enabled: !!center?.id
  })
  const journeyPlace = center?.journeyPlace
  const timezone = journeyPlace?.timezone ?? journeyPlace?.country.timezone

  const startDate = center?.startDate ? DateFormatter.DDMMYYYY(center.startDate) : ''
  const endDate = center?.endDate ? DateFormatter.DDMMYYYY(center.endDate) : ''
  const centerNameWithDates = `${center?.name}${startDate && endDate ? ` - ${startDate} au ${endDate}` : ''}`

  if (!center || !id || !centerCurrency) return <LoadingScreen />

  return (
    <CurrencyProvider currency={centerCurrency.currency}>
      <UserNavBar icon={BuildingThreeIcon} title={centerNameWithDates} />
      <Box className="mt-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              icon={ChevronLeftIcon}
              label={t('ol.center.back.button')}
              variant="secondary"
              onClick={() => back()}
            />
            <Typography.Link
              variant="secondary-body"
              onClick={() => {
                window.open(
                  constructCenterPath(Paths.CENTER_GLOBAL_VIEW_PAGE, center.id),
                  '_blank'
                )
              }}
            >
              Accéder au centre vue générique
            </Typography.Link>
          </div>
          {center.ol && (
            <Badge
              variant="white"
              size="flexible"
              className="rounded"
              iconPosition="start"
              icon={<Badge icon={<ProviderIcon color={tailwindColors.grey.lighter} />} />}
              text={
                <div className="flex gap-2 items-center">
                  <Typography variant="label" color={tailwindColors.grey.lighter}>
                    {t('ol.center.prestataire')}
                  </Typography>
                  <TextLink
                    href={Paths.OL_DETAILS_PAGE.replace(':id', center.ol.id.toString()).replace(
                      '*',
                      'update'
                    )}
                    name={getOlName(center.ol, { allUpperCase: true })}
                    className="text-grey"
                    options={{ rel: 'noreferrer', target: '_blank' }}
                  />
                  {center.ol.mainContactPhone && (
                    <Typography variant="label" color={tailwindColors.grey.DEFAULT}>
                      ({formatPhoneNumber(center.ol.mainContactPhone)})
                    </Typography>
                  )}
                  <div className="w-2" />
                </div>
              }
            />
          )}
        </div>
        <div className="h-4" />
        <OlCenterJourneyBadgeDetails center={center} />
        <div className="h-4" />

        {/** Tabs for the center details */}
        <OlCenterCapsuleTab centerId={+id} />

        {/**Tab View  */}
        <Routes>
          <Route
            path={Paths.OL_CENTER_PLACEMENTS}
            element={<CenterPlacementAndDiffusionForParents id={+id} />}
          />
          <Route
            path={Paths.CENTER_GROUPS_PAGE}
            element={<CenterGroups id={center.id} mode="edit" />}
          />
          <Route
            path={Paths.OL_CENTER_COST}
            element={<CostOfCenterPage data={center} refetch={refetch} />}
          />
          <Route
            path={Paths.OL_CENTER_ACTIVITIES_TEFL_AND_TEACHING_HALLS}
            element={
              <TimezoneProvider defaultTimezone={timezone}>
                <CenterProgramAndActivities data={center} refetch={refetch} />
              </TimezoneProvider>
            }
          />
          <Route
            path={Paths.OL_CENTER_TRIP_INFORMATION}
            element={<OlCenterTripsInfo id={+id} />}
          />
          <Route
            path={Paths.OL_CENTER_FINAL_COST}
            element={<FinalCostOfCenterPage data={center} refetch={refetch} />}
          />
          <Route
            path={Paths.OL_CENTER_EVENTS}
            element={
              getOlPermissions({ role: user.role, type: center.ol?.type })['center-events'] && (
                <div className="mt-5">
                  <Typography variant="sub-heading" color={tailwindColors.black.DEFAULT}>
                    Evènements en séjour
                  </Typography>
                  <div className="h-3" />

                  <CenterEventTable centerId={center.id} version={'OL_VIEW'} headerType="none" />
                </div>
              )
            }
          />
          <Route
            path={Paths.OL_CENTER_END_OF_STAY_APPRECIATIONS}
            element={
              user.role === RoleEnum.ADMIN && <CenterEndOfStayAppreciations centerId={center.id} />
            }
          />
          <Route index element={<Navigate to={Paths.OL_CENTER_PLACEMENTS} />} />
          <Route path={'*'} element={<NotFoundPage />} />
        </Routes>
      </Box>
    </CurrencyProvider>
  )
}

export default withCenter(OlCenterDetails)
